import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import './App.css';
import SingBoxGuide from './SingBoxGuide';
import DebugLogger from './DebugLogger';
import TonPayment from './TonPayment';
import SupportTickets from './SupportTickets';
import LanguageSwitcher from './components/LanguageSwitcher';
import { useTranslation } from './i18n';
import { API_BASE_URL, FULL_VERSION, DEBUG_MODE, debugLog, ENABLE_SMART_ROUTER } from './config';
import { smartAPI } from './smartAPI';

// Helper: Smart fetch с автоматическим failover
const apiFetch = async (path, options = {}) => {
  if (ENABLE_SMART_ROUTER) {
    // Используем SmartAPI с автоматическим failover
    return smartAPI.request(path, options);
  } else {
    // Legacy режим: прямой fetch к API_BASE_URL
    return fetch(`${API_BASE_URL}${path}`, options);
  }
};

// Определяем источник сервера
const getServerSource = () => {
  const hostname = (window.location.hostname || '').toLowerCase();
  // Mini App (dedicated nginx)
  if (hostname.includes('miniapp.shurik.ch')) return 'Mini App';
  if (hostname.includes('miniapp-dev.shurik.ch')) return 'Mini App Dev';
  // Legacy: Mini App served from CRM
  if (hostname.includes('vpn-admin.shurik.ch')) return 'Main Server';
  // VDS Mirror (RU) — legacy
  if (hostname.includes('ru-mr.shurik.ch')) return 'VDS Server';
  // Yandex Cloud Mirror (RU)
  if (hostname.includes('apigw.yandexcloud.net')) return 'Yandex Mirror';
  // CDN mirror
  if (hostname.includes('surge.sh')) return 'Surge CDN';
  // Cloudflare Pages
  if (hostname.includes('vpn-beta.shurik.ch')) return 'Cloudflare';
  if (hostname.includes('pages.dev')) return 'Staging';
  // VDS container
  if (hostname.includes('85.159.231.153')) return 'VDS Container';
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) return 'Local Dev';
  return 'Unknown Server';
};

const SERVER_SOURCE = getServerSource();

// Функция для отправки логов на сервер (только в DEBUG режиме)
const sendLog = async (level, message, details = {}) => {
  if (!DEBUG_MODE) return; // Skip в production
  
  try {
    await apiFetch('/api/miniapp/log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        level,
        message,
        details: {
          ...details,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          url: window.location.href
        }
      })
    });
  } catch (err) {
    // Silent fail в production, log только в DEBUG режиме
    if (DEBUG_MODE) {
    console.error('Failed to send log:', err);
    }
  }
};

function App() {
  // i18n hook for translations
  const { t } = useTranslation();

  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authData, setAuthData] = useState(null);
  
  // Состояния для навигации
  const [activeTab, setActiveTab] = useState('dashboard'); // 'dashboard', 'payment', 'support'
  
  // Состояния для промокодов
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoMessage, setPromoMessage] = useState('');
  
  // Удалены неиспользуемые состояния для конфигураций (теперь используем мультисерверную подписку)
  
  // Состояния для OpenConnect
  const [openconnectModal, setOpenconnectModal] = useState(false);
  const [openconnectConfig, setOpenconnectConfig] = useState(null);
  const [openconnectLoading, setOpenconnectLoading] = useState(false);

  // Состояния для оплаты
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('rollypay');
  const [lastPaymentTs, setLastPaymentTs] = useState(0);
  const pendingStarsPaymentIdRef = useRef(null);

  // Состояния для TON оплаты
  const [showTonPayment, setShowTonPayment] = useState(false);
  const [tonPaymentPlanId, setTonPaymentPlanId] = useState(null);

  // Memoized authData for TonPayment to prevent polling reset on re-render
  // Note: initData is set once on WebApp load and never changes during session
  const tonAuthData = useMemo(() => ({
    ...authData?.user,
    initData: window.Telegram?.WebApp?.initData
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [authData?.user]);

  // Memoized callbacks for TonPayment to prevent polling reset on re-render
  const handleTonSuccess = useCallback(() => {
    setShowTonPayment(false);
    setTonPaymentPlanId(null);
    // Refresh user data
    window.location.reload();
  }, []);

  const handleTonError = useCallback((err) => {
    debugLog('TON payment error:', err);
  }, []);

  const handleTonCancel = useCallback(() => {
    setShowTonPayment(false);
    setTonPaymentPlanId(null);
  }, []);
  
  // Инструкция по Sing-Box
  const [showSingBoxGuide, setShowSingBoxGuide] = useState(false);
  
  // Тикеты поддержки
  const [showSupportTickets, setShowSupportTickets] = useState(false);

  // Dynamic prices from API (fallback to defaults)
  const [prices, setPrices] = useState({
    vpn_month: { price_rub: 100, price_stars: 102, price_ton: 1.0 },
    vpn_year:  { price_rub: 1000, price_stars: 1012, price_ton: 10.0 },
  });

  useEffect(() => {
    apiFetch('/api/plans')
      .then(r => r.json())
      .then(data => {
        if (data.success && data.plans) {
          setPrices(prev => ({ ...prev, ...data.plans }));
        }
      })
      .catch(() => {});
  }, []);

  const getStarsPrice = (planId) => prices[planId]?.price_stars || 0;
  const getTonPrice = (planId) => prices[planId]?.price_ton || 0;
  const getRubPrice = (planId) => prices[planId]?.price_rub || 0;

  const getPriceDisplay = (planId) => {
    if (selectedPaymentMethod === 'telegram_stars') {
      return `${getStarsPrice(planId)} ⭐`;
    } else if (selectedPaymentMethod === 'ton') {
      return `${getTonPrice(planId)} TON 💎`;
    }
    return `${getRubPrice(planId)}₽`;
  };


  useEffect(() => {
    // Настройка Telegram WebApp
    const telegramAvailable = !!(window.Telegram && window.Telegram.WebApp);
    let tg = null;
    let onInvoiceClosed = null;

    if (telegramAvailable) {
      tg = window.Telegram.WebApp;

      // Настройка темы
      tg.setHeaderColor('#667eea');
      tg.setBackgroundColor('#ffffff');

      // Расширяем приложение
      tg.expand();

      // Включаем кнопку закрытия
      tg.enableClosingConfirmation();

      debugLog('Mini App initialized in Telegram');

      // Авто-обновление статуса после оплаты Stars
      onInvoiceClosed = async (e) => {
        try {
          if (e && e.status === 'paid') {
            sendLog('info', 'Invoice closed: paid', e);
            const paymentId = pendingStarsPaymentIdRef.current;
            const initData = window.Telegram?.WebApp?.initData;
            const u = window.Telegram?.WebApp?.initDataUnsafe?.user;
            if (paymentId && initData && u?.id) {
              try {
                const authDataForServer = { initData, ...u };
                const syncRes = await apiFetch('/api/telegram/stars/sync-payment', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    auth_data: authDataForServer,
                    payment_id: paymentId,
                  }),
                });
                const syncJson = await syncRes.json().catch(() => ({}));
                sendLog('info', 'Stars sync-payment after invoice', {
                  ok: syncJson.success,
                  detail: syncJson.detail || syncJson.error,
                });
              } catch (syncErr) {
                sendLog('warn', 'Stars sync-payment request failed', syncErr);
              }
            }
            pendingStarsPaymentIdRef.current = null;
            setActiveTab('dashboard');
            setTimeout(() => { refreshUserStatus(); }, 800);
          }
        } catch (err) {}
      };
      try { tg.onEvent('invoiceClosed', onInvoiceClosed); } catch (_) {}
    }

    // Инициализация авторизации должна выполняться всегда
    initializeAuth();

    // Очистка обработчиков при размонтировании
    return () => {
      try { if (tg && onInvoiceClosed) tg.offEvent('invoiceClosed', onInvoiceClosed); } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Обновляем пользователя при возврате в приложение (после оплаты)
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && lastPaymentTs && (Date.now() - lastPaymentTs < 3 * 60 * 1000)) {
        sendLog('info', 'Visibility change after payment, refreshing user');
        refreshUserStatus();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [lastPaymentTs]);

  const initializeAuth = async () => {
    debugLog('[DEBUG] 🚀 initializeAuth START');
    
    // 🔥 КРИТИЧЕСКИ ВАЖНО: Вызываем ready() ДО любых операций с SDK!
    if (window.Telegram?.WebApp) {
      debugLog('[DEBUG] 📞 Calling Telegram.WebApp.ready()...');
      try {
        window.Telegram.WebApp.ready();
        debugLog('[DEBUG] ✅ Telegram.WebApp.ready() called successfully');
        
        // Даём SDK время инициализироваться после ready()
        await new Promise(resolve => setTimeout(resolve, 300));
        
        debugLog('[DEBUG] 📊 After ready(), initData available:', {
          hasInitData: !!window.Telegram.WebApp.initData,
          initDataLength: window.Telegram.WebApp.initData?.length || 0,
          hasUser: !!window.Telegram.WebApp.initDataUnsafe?.user,
          userDetails: window.Telegram.WebApp.initDataUnsafe?.user ? {
            id: window.Telegram.WebApp.initDataUnsafe.user.id,
            username: window.Telegram.WebApp.initDataUnsafe.user.username
          } : null
        });
      } catch (readyError) {
        console.error('[DEBUG] ❌ ready() call failed:', readyError);
      }
    }
    
    // 🔑 PRE-APP ЛОГИКА: Сохраняем initData в hash если его там нет (как в pre-app/index.html:522-525)
    // Это гарантирует что мы захватим initData пока SDK точно готов
    try {
      const currentHash = window.location.hash;
      const hashParams = new URLSearchParams(currentHash.substring(1));
      const existingTgWebAppData = hashParams.get('tgWebAppData');
      
      if (!existingTgWebAppData && window.Telegram?.WebApp?.initData) {
        debugLog('[DEBUG] 💾 Pre-app logic: Saving initData to hash for reliability');
        debugLog('[DEBUG] 📊 initData length:', window.Telegram.WebApp.initData.length);
        
        // 📱 ВИДИМОЕ УВЕДОМЛЕНИЕ для мобильного (БЕЗ setLoading - он блокирует error!)
        setError('🔄 Сохраняем данные... (Pre-app логика)');
        
        // Даём время пользователю увидеть сообщение
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Сохраняем initData в hash (как делает Pre-app) и перезагружаем
        const newHash = `#tgWebAppData=${encodeURIComponent(window.Telegram.WebApp.initData)}`;
        debugLog('[DEBUG] 🔄 Reloading with hash to preserve initData...');
        
        // Перезагружаем страницу с hash
        window.location.hash = newHash;
        window.location.reload();
        return; // Прерываем выполнение, ждём перезагрузки
      }
      
      debugLog('[DEBUG] 🔍 Hash check:', {
        hasHash: !!existingTgWebAppData,
        hashLength: existingTgWebAppData?.length || 0,
        currentHashRaw: window.location.hash.substring(0, 50) + '...',
        hasInitDataInSDK: !!window.Telegram?.WebApp?.initData,
        initDataLength: window.Telegram?.WebApp?.initData?.length || 0
      });
      
      // 📱 ВИДИМОЕ УВЕДОМЛЕНИЕ если есть hash
      if (existingTgWebAppData) {
        setError('✅ Загружаем из hash (Pre-app режим)');
        await new Promise(resolve => setTimeout(resolve, 300));
        setError(null);
      }
    } catch (hashError) {
      console.error('[DEBUG] ❌ Hash preservation error:', hashError);
      setError(`⚠️ Hash error: ${hashError.message}`);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setError(null);  // Очистка обязательна для транзитных сообщений (см. docs/architecture.md)
      // Продолжаем работу даже если не удалось сохранить в hash
    }
    
    try {
    debugLog('[DEBUG] 🔍 Checking Telegram WebApp:', {
      hasTelegram: !!window.Telegram,
      hasWebApp: !!window.Telegram?.WebApp,
      hasInitData: !!window.Telegram?.WebApp?.initData,
      initDataLength: window.Telegram?.WebApp?.initData?.length || 0
    });
    
    // 🔄 Ждём инициализации Telegram WebApp SDK (важно для прямого запуска)
    if (window.Telegram?.WebApp && !window.Telegram?.WebApp?.initData) {
      debugLog('[DEBUG] ⏳ Waiting for Telegram WebApp to initialize...');
      sendLog('info', 'Waiting for Telegram WebApp to initialize...');
      
      // Даём SDK время на инициализацию (максимум 3 секунды)
      let attempts = 0;
      const maxAttempts = 30;
      
      while (attempts < maxAttempts && !window.Telegram?.WebApp?.initData) {
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
        debugLog(`[DEBUG] ⏳ Attempt ${attempts}/${maxAttempts}, initData: ${!!window.Telegram?.WebApp?.initData}`);
      }
      
      if (!window.Telegram?.WebApp?.initData) {
        debugLog('[DEBUG] ⚠️ Telegram WebApp initData still empty after waiting');
        sendLog('warning', 'Telegram WebApp initData still empty after waiting', {
          attempts,
          waitedMs: attempts * 100
        });
      } else {
        debugLog('[DEBUG] ✅ Telegram WebApp initialized successfully');
        sendLog('success', 'Telegram WebApp initialized successfully', {
          attempts,
          waitedMs: attempts * 100,
          initDataLength: window.Telegram?.WebApp?.initData?.length || 0
        });
      }
    }
    
    const telegramAvailable = !!(window.Telegram && window.Telegram.WebApp);
    const hasTelegramData = telegramAvailable && window.Telegram.WebApp.initDataUnsafe?.user;
    
      // Проверяем токен авторизации
    let authToken = null;
    if (telegramAvailable && window.Telegram.WebApp.initDataUnsafe?.start_param) {
      authToken = window.Telegram.WebApp.initDataUnsafe.start_param;
    }
    
    if (!authToken) {
      const urlParams = new URLSearchParams(window.location.search);
      authToken = urlParams.get('token');
    }
    
      sendLog('info', 'Mini App Auth Context', {
      telegramAvailable,
      hasTelegramData,
        authToken: authToken ? `${authToken.substring(0, 8)}...` : null
      });

      if (telegramAvailable && hasTelegramData) {
        let telegramAuth;
        
        // Проверяем hash параметр с данными от Pre-App (как в коммите f0b4e90)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const tgWebAppData = hashParams.get('tgWebAppData');
        
        if (tgWebAppData) {
          // Используем данные переданные от Pre-App
          telegramAuth = decodeURIComponent(tgWebAppData);
          sendLog('info', 'Using Telegram data from Pre-App', { hasData: !!telegramAuth });
        } else {
          // Пытаемся получить данные напрямую
            telegramAuth = window.Telegram.WebApp.initData;
          sendLog('info', 'Using Telegram data directly', { hasData: !!telegramAuth });
        }
        
        // Проверяем что данные получены
        if (!telegramAuth || telegramAuth.length === 0) {
          const errorMsg = 'Telegram initData is empty or unavailable';
          console.error('[DEBUG]', errorMsg);
          sendLog('error', errorMsg, {
            hasTelegramWebApp: !!window.Telegram?.WebApp,
            hasInitData: !!window.Telegram?.WebApp?.initData,
            initDataLength: window.Telegram?.WebApp?.initData?.length || 0
          });
          setError(t('errors.noTelegramData'));
          setLoading(false);
          return;
          }
        
        setAuthData(window.Telegram.WebApp.initDataUnsafe);

        // Отправляем данные на сервер для аутентификации
        const userTelegram = window.Telegram.WebApp.initDataUnsafe.user;
        
        // 🔍 Определяем провайдера и предпочитаемый сервер
        const userAgent = navigator.userAgent || '';
        const isBeeline = userAgent.toLowerCase().includes('beeline') || 
                         userAgent.toLowerCase().includes('билайн') ||
                         window.location.hostname.includes('beeline');
        
        // 🏷️ Определяем предпочитаемый сервер
        const preferredServer = isBeeline ? 'vds' : 'main';
        
        sendLog('info', 'User Context Detection', {
          userAgent: userAgent.substring(0, 100),
          isBeeline,
          preferredServer,
          hostname: window.location.hostname
        });
        
        debugLog('[DEBUG] Sending auth request to:', `${API_BASE_URL}/api/telegram/auth`);
        debugLog('[DEBUG] Request body:', { 
          hasInitData: !!telegramAuth, 
          initDataLength: telegramAuth?.length,
          userId: userTelegram.id,
          username: userTelegram.username
        });
        
        try {
          const response = await apiFetch('/api/telegram/auth', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-User-Agent': userAgent.substring(0, 100),
            'X-Is-Beeline': isBeeline ? 'true' : 'false',
            'X-Preferred-Server': preferredServer
          },
          body: JSON.stringify({
            initData: telegramAuth,
            token: authToken,
            // Добавляем данные пользователя
            id: userTelegram.id,
            first_name: userTelegram.first_name,
            last_name: userTelegram.last_name,
            username: userTelegram.username,
            photo_url: userTelegram.photo_url
          })
        });

          debugLog('[DEBUG] Response status:', response.status, response.statusText);

        const data = await response.json();
          
          debugLog('[DEBUG] Response data:', data);
          
        sendLog('info', 'Auth API Response', { 
          success: data.success, 
          username: data.username,
          display_name: data.display_name,
          first_name: data.first_name,
          plan: data.plan,
          expiry_date: data.expiry_date,
          responseKeys: Object.keys(data)
        });
        
        if (data.success) {
          // Фильтруем "None" значения от API
          const cleanData = {
            ...data,
            username: data.username === 'None' ? null : data.username,
            display_name: data.display_name === 'None' ? null : data.display_name,
            first_name: data.first_name === 'None' ? null : data.first_name
          };
          
          setUser(cleanData);
          if (telegramAvailable) {
            const userName = cleanData.display_name || cleanData.username || cleanData.first_name || 'User';
            // Используем более ненавязчивое уведомление
            window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
            setTimeout(() => {
              if (window.Telegram?.WebApp?.showPopup) {
                window.Telegram.WebApp.showPopup({
                  title: `👋 ${t('welcome.title')}`,
                  message: t('welcome.greeting', { name: userName }),
                  buttons: [
                    {
                      id: 'ok',
                      type: 'default',
                      text: t('welcome.start')
                    }
                  ]
                }, () => {
                  // Остаемся на текущей вкладке
                });
          }
            }, 500);
          }
        } else {
          const authError = data.error || t('errors.auth');
          setError(authError);
          
          // 🔧 ПРИНУДИТЕЛЬНАЯ АВТОРИЗАЦИЯ для проблемных соединений (Beeline)
          if (authError.includes('Failed to fetch') || authError.includes('connection')) {
            sendLog('warning', 'Auth failed, starting auto force auth for network issues', {
              error: authError,
              isBeeline,
              preferredServer,
              willRetryIn: '2 seconds',
              authMethod: 'automatic'
            });
            
            setTimeout(() => {
              sendLog('info', 'Executing automatic force auth', { userId: userTelegram.id });
              forceAuth(userTelegram, isBeeline, preferredServer).catch((forceError) => {
                sendLog('error', 'Automatic force auth failed', { error: forceError.message });
              });
            }, 2000);
          }
        }
        } catch (fetchError) {
          // Ловим все ошибки связанные с fetch запросом
          console.error('[DEBUG] Auth fetch error:', fetchError);
          console.error('[DEBUG] Fetch error details:', {
            message: fetchError.message,
            name: fetchError.name,
            stack: fetchError.stack?.substring(0, 200),
            apiUrl: `${API_BASE_URL}/api/telegram/auth`,
            currentOrigin: window.location.origin,
            currentHref: window.location.href
          });
          sendLog('error', 'Auth Fetch Error', {
            message: fetchError.message,
            stack: fetchError.stack || '',
            type: fetchError.constructor.name,
            apiBaseUrl: API_BASE_URL,
            url: `${API_BASE_URL}/api/telegram/auth`
          });
          
          setError(`${t('errors.connection')}: ${fetchError.message || 'Load failed'}. ${t('errors.connectionMessage')}`);
          setLoading(false);
          return;
        }
      } else {
        // Web-версия или другой контекст (обычный браузер)
        sendLog('info', 'Browser Access Detected', {
          userAgent: navigator.userAgent,
          hostname: window.location.hostname,
          isMirror: window.location.hostname.includes('mirror-shurik')
        });
        
        // Показываем информационное сообщение для браузера
        setError(`🪞 ${t('errors.browserAccess')}`);
        return; // Не делаем запрос к API
      }
          } catch (err) {
        sendLog('error', 'Auth Error', {
          message: err.message,
          stack: err.stack,
          cause: err.cause,
          type: err.constructor.name
        });
        setError(`${t('errors.connection')}: ${err.message}`);
        
        // 🔧 ПРИНУДИТЕЛЬНАЯ АВТОРИЗАЦИЯ при network errors (особенно для Beeline)
        if (err.message.includes('fetch') || err.message.includes('Failed')) {
          const telegramData = window.Telegram?.WebApp?.initDataUnsafe?.user;
          if (telegramData) {
            const userAgent = navigator.userAgent || '';
            const isBeeline = userAgent.toLowerCase().includes('beeline') || 
                             userAgent.toLowerCase().includes('билайн');
            const preferredServer = isBeeline ? 'vds' : 'main';
            
            sendLog('warning', 'Network error detected, starting auto force auth', {
              error: err.message,
              isBeeline,
              preferredServer,
              willRetryIn: '3 seconds',
              authMethod: 'automatic'
            });
            
            setTimeout(() => {
              sendLog('info', 'Executing automatic force auth for network error', { userId: telegramData.id });
              forceAuth(telegramData, isBeeline, preferredServer).catch((forceError) => {
                sendLog('error', 'Network error force auth failed', { error: forceError.message });
              });
            }, 3000);
          }
        }
      } finally {
      setLoading(false);
    }
  };

  // Мягкое обновление профиля пользователя без popup
  const refreshUserStatus = async () => {
    try {
      const tg = window.Telegram?.WebApp;
      if (!tg?.initDataUnsafe?.user) return;
      const telegramAuth = tg.initData;
      const userTelegram = tg.initDataUnsafe.user;

      const userAgent = navigator.userAgent || '';
      const isBeeline = userAgent.toLowerCase().includes('beeline') || userAgent.toLowerCase().includes('билайн') || window.location.hostname.includes('beeline');
      const preferredServer = isBeeline ? 'vds' : 'main';

      const resp = await apiFetch('/api/telegram/auth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Agent': userAgent.substring(0, 100),
          'X-Is-Beeline': isBeeline ? 'true' : 'false',
          'X-Preferred-Server': preferredServer
        },
        body: JSON.stringify({
          initData: telegramAuth,
          token: null,
          id: userTelegram.id,
          first_name: userTelegram.first_name,
          last_name: userTelegram.last_name,
          username: userTelegram.username,
          photo_url: userTelegram.photo_url
        })
      });
      const data = await resp.json();
      if (data && data.success) {
        const cleanData = {
          ...data,
          username: data.username === 'None' ? null : data.username,
          display_name: data.display_name === 'None' ? null : data.display_name,
          first_name: data.first_name === 'None' ? null : data.first_name
        };
        setUser(cleanData);
      }
    } catch (e) {}
  };

  const applyPromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoMessage(t('dashboard.promo.empty'));
      return;
    }

    setPromoLoading(true);
    setPromoMessage('');

    try {
      // Подготавливаем данные для аутентификации
      const authDataForServer = {
        initData: window.Telegram.WebApp.initData, // строка initData
        ...authData.user // добавляем данные пользователя напрямую
      };

      const response = await apiFetch('/api/telegram/apply-promo', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          auth_data: authDataForServer,
          promo_code: promoCode.trim()
        })
      });

      const data = await response.json();
      
            if (data.success) {
        setPromoMessage(data.message);
        setPromoCode('');
        // Обновляем информацию о пользователе
        setTimeout(() => {
          window.location.reload();
        }, 1500);
            } else {
        setPromoMessage(data.error || t('dashboard.promo.error'));
            }
    } catch (err) {
      sendLog('error', 'Promo Code Error', {
        message: err.message,
        promoCode: promoCode.substring(0, 3) + '***' // Частично скрываем промокод
      });
      setPromoMessage(t('errors.connectionServer'));
    } finally {
      setPromoLoading(false);
    }
  };

  const addToSingBox = async (protocol) => {
    if (!user) return;
    
    try {
      // 📋 Используем мультисерверную подписку вместо отдельных конфигов
      const subscriptionUrl = `https://vpn-admin.shurik.ch/api/sing-box/subscription/${user.username}`;
      const profileName = encodeURIComponent(`🌍 VPN Shurik - ${user.username}`);
      const singBoxUrl = `sing-box://import-remote-profile?url=${encodeURIComponent(subscriptionUrl)}#${profileName}`;
      
      // Пробуем разные способы открытия sing-box ссылки
      let opened = false;
    
      // Способ 1: Попробуем создать скрытую ссылку и кликнуть
      try {
        const link = document.createElement('a');
        link.href = singBoxUrl;
        link.target = '_blank';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        opened = true;
      } catch (e) {
        // Если не сработало, пробуем window.open
        try {
          window.open(singBoxUrl, '_blank');
          opened = true;
        } catch (e2) {
          // Если ничего не сработало, показываем диалог с инструкциями
          opened = false;
      }
    }
    
      if (opened) {
        sendLog('info', 'SingBox Add Subscription', {
          subscription: 'multi-server',
          username: user?.username,
          url: singBoxUrl
        });
        
        if (window.Telegram?.WebApp) {
          window.Telegram.WebApp.showPopup({
            title: `🌍 ${t('singbox.popups.added.title')}`,
            message: t('singbox.popups.added.message'),
            buttons: [
              {
                id: 'ok',
                type: 'default',
                text: t('common.confirm')
              }
            ]
          }, () => {
            setTimeout(() => {
              setActiveTab('dashboard');
            }, 100);
          });
      } else {
          setActiveTab('dashboard');
      }
      } else {
        // Просто возвращаемся к дашборду если sing-box не открылся
        if (window.Telegram?.WebApp) {
          window.Telegram.WebApp.showPopup({
            title: `📱 ${t('singbox.popups.notOpened.title')}`,
            message: t('singbox.popups.notOpened.message'),
            buttons: [
              {
                id: 'ok',
                type: 'default',
                text: t('common.confirm')
              }
            ]
          }, (buttonId) => {
            setTimeout(() => {
              setActiveTab('dashboard');
            }, 100);
          });
      } else {
          setActiveTab('dashboard');
        }
        
        sendLog('info', 'SingBox Fallback - App Not Opened', {
          protocol: protocol,
          username: user?.username
        });
      }
      
    } catch (err) {
      sendLog('error', 'SingBox Add Error', {
        message: err.message,
        protocol: protocol,
        username: user?.username
      });
      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.showPopup({
          title: `❌ ${t('singbox.popups.error.title')}`,
          message: t('singbox.popups.error.message'),
          buttons: [
            {
              id: 'ok',
              type: 'default',
              text: t('common.confirm')
            }
          ]
        }, (buttonId) => {
          setActiveTab('dashboard');
        });
      } else {
        setActiveTab('dashboard');
      }
    }
  };

  const addToHiddify = async () => {
    if (!user) return;

    try {
      const subscriptionUrl = `https://vpn-admin.shurik.ch/api/sing-box/subscription/${user.username}`;
      const hiddifyUrl = `hiddify://import/${subscriptionUrl}`;

      let opened = false;

      try {
        const link = document.createElement('a');
        link.href = hiddifyUrl;
        link.target = '_blank';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        opened = true;
      } catch (e) {
        try {
          window.open(hiddifyUrl, '_blank');
          opened = true;
        } catch (e2) {
          opened = false;
        }
      }

      if (opened) {
        sendLog('info', 'Hiddify Add Subscription', {
          subscription: 'multi-server',
          username: user?.username,
          url: hiddifyUrl
        });

        if (window.Telegram?.WebApp) {
          window.Telegram.WebApp.showPopup({
            title: `🌍 ${t('hiddify.popups.added.title')}`,
            message: t('hiddify.popups.added.message'),
            buttons: [{ id: 'ok', type: 'default', text: t('common.confirm') }]
          }, () => {
            setTimeout(() => setActiveTab('dashboard'), 100);
          });
        } else {
          setActiveTab('dashboard');
        }
      } else {
        if (window.Telegram?.WebApp) {
          window.Telegram.WebApp.showPopup({
            title: `📱 ${t('hiddify.popups.notOpened.title')}`,
            message: t('hiddify.popups.notOpened.message'),
            buttons: [{ id: 'ok', type: 'default', text: t('common.confirm') }]
          }, () => {
            setTimeout(() => setActiveTab('dashboard'), 100);
          });
        } else {
          setActiveTab('dashboard');
        }

        sendLog('info', 'Hiddify Fallback - App Not Opened', {
          username: user?.username
        });
      }
    } catch (err) {
      sendLog('error', 'Hiddify Add Error', {
        message: err.message,
        username: user?.username
      });
      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.showPopup({
          title: `❌ ${t('hiddify.popups.error.title')}`,
          message: t('hiddify.popups.error.message'),
          buttons: [{ id: 'ok', type: 'default', text: t('common.confirm') }]
        }, () => {
          setActiveTab('dashboard');
        });
      } else {
        setActiveTab('dashboard');
      }
    }
  };

  const showOpenConnectConfig = async () => {
    if (!user || !authData) return;
    
    setOpenconnectLoading(true);
    try {
      const response = await apiFetch('/api/telegram/openconnect-config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          auth_data: {
            initData: window.Telegram.WebApp.initData,
            ...authData.user
          }
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setOpenconnectConfig(data.config);
        setOpenconnectModal(true);
        
        sendLog('info', 'OpenConnect Config Requested', {
          username: user?.username,
          server: data.config.server
        });
      } else {
        alert(`${t('errors.openconnectConfig')}: ${data.error}`);
      }
    } catch (error) {
      sendLog('error', 'OpenConnect Config Error', {
        username: user?.username,
        error: error.message
      });
      
      alert(`${t('errors.openconnectConfig')}: ${error.message}`);
    } finally {
      setOpenconnectLoading(false);
    }
  };

  const copyToClipboard = (text, description) => {
    navigator.clipboard.writeText(text).then(() => {
      alert(t('clipboard.copied', { item: description }));
    }).catch(() => {
      // Fallback для старых браузеров
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert(t('clipboard.copied', { item: description }));
    });
  };

  const createPayment = async (planId, amount) => {
    sendLog('info', 'Creating Payment', {
      planId: planId,
      amount: amount,
      paymentMethod: selectedPaymentMethod
    });

    // Для TON показываем специальный компонент оплаты
    if (selectedPaymentMethod === 'ton') {
      // Guard: ensure authData is loaded before showing TON payment modal
      if (!authData?.user?.id) {
        debugLog('TON payment error: authData not loaded');
        if (window.Telegram?.WebApp?.showAlert) {
          window.Telegram.WebApp.showAlert(t('errors.authReload'));
        }
        return;
      }
      setTonPaymentPlanId(planId);
      setShowTonPayment(true);
      return;
    }
    
    try {
      // Подготавливаем правильные данные авторизации для сервера
      const authDataForServer = {
        initData: window.Telegram.WebApp.initData, // строка initData для проверки подписи
        ...authData.user // добавляем данные пользователя напрямую
      };
      
      const response = await apiFetch('/api/telegram/renew', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          auth_data: authDataForServer,
          plan_id: planId,
          amount: amount,
          payment_method: selectedPaymentMethod
        })
      });
      
      const data = await response.json();
      
      sendLog('info', 'Payment API Response', {
        success: data.success,
        paymentMethod: selectedPaymentMethod,
        hasPaymentUrl: !!data.payment_url,
        hasInvoiceLink: !!data.invoice_link,
        error: data.error
      });
      
      if (data.success) {
        if (selectedPaymentMethod === 'telegram_stars') {
          // Открываем инвойс по ссылке (flow без необходимости /start у бота)
          const link = data.invoice_link;
          if (data.payment_id) {
            pendingStarsPaymentIdRef.current = data.payment_id;
          }
          if (link) {
            if (window.Telegram?.WebApp?.openInvoice) {
              window.Telegram.WebApp.openInvoice(link);
            } else if (window.Telegram?.WebApp?.openLink) {
              window.Telegram.WebApp.openLink(link);
            } else {
              window.open(link, '_blank');
            }
            setLastPaymentTs(Date.now());
          } else if (window.Telegram?.WebApp) {
            window.Telegram.WebApp.showPopup({
              title: `❌ ${t('common.error')}`,
              message: t('errors.paymentLink'),
              buttons: [{ id: 'ok', type: 'default', text: t('common.confirm') }]
            });
          }
        } else {
          // Для других методов (YooMoney) открываем ссылку
          sendLog('info', 'YooMoney Payment', {
            planId: planId,
            amount: amount,
            paymentUrl: data.payment_url
          });
          
          if (window.Telegram?.WebApp) {
            // В Telegram WebApp используем openLink для внешних ссылок
            window.Telegram.WebApp.openLink(data.payment_url);
          } else {
            // В обычном браузере используем window.open
            window.open(data.payment_url, '_blank');
          }
        }
      } else {
        if (window.Telegram?.WebApp) {
          window.Telegram.WebApp.showPopup({
            title: `❌ ${t('common.error')}`,
            message: data.error || t('payment.errors.failed'),
            buttons: [
              {
                id: 'ok',
                type: 'default',
                text: t('common.confirm')
              }
            ]
          }, (buttonId) => {
            setActiveTab('payment');
          });
      } else {
          setActiveTab('payment');
        }
      }
    } catch (err) {
      sendLog('error', 'Payment Error', {
        message: err.message,
        planId: planId,
        amount: amount,
        paymentMethod: selectedPaymentMethod
      });
      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.showPopup({
          title: `❌ ${t('errors.connection')}`,
          message: t('payment.errors.connection'),
          buttons: [
            {
              id: 'ok',
              type: 'default',
              text: t('common.confirm')
            }
          ]
        }, (buttonId) => {
          setActiveTab('payment');
        });
    } else {
        setActiveTab('payment');
      }
    }
  };



  const formatTimeRemaining = (expiry) => {
    if (!expiry) return t('time.unknown');
    
    const now = new Date();
    const expiryDate = new Date(expiry);
    const diff = expiryDate - now;
    
    if (diff <= 0) return t('time.expired');
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return t('time.daysRemaining', { count: days });
    return t('time.hoursRemaining', { count: hours });
  };

  // Рендер Dashboard
  const renderDashboard = () => (
    <div className="tab-content">
      <div className="dashboard-header">
        <div className="user-avatar">
          {user?.display_name?.charAt(0) || user?.username?.charAt(0) || user?.first_name?.charAt(0) || 'U'}
        </div>
        <div className="user-info">
          <h2>{user?.display_name || user?.username || user?.first_name || t('dashboard.title')}</h2>
          <p className="user-plan">{user?.plan || t('dashboard.noPlan')}</p>
        </div>
      </div>

      <div className="status-card">
        <div className="status-icon">
          {new Date(user?.expiry_date) > new Date() ? '🟢' : '🔴'}
        </div>
        <div className="status-info">
          <h3>{t('dashboard.subscription.title')}</h3>
          <p className={`status-text ${new Date(user?.expiry_date) > new Date() ? 'active' : 'expired'}`}>
            {new Date(user?.expiry_date) > new Date() ? t('dashboard.subscription.active') : t('dashboard.subscription.expired')}
          </p>
          <p className="expiry-date">
            {t('dashboard.subscription.until', { date: formatTimeRemaining(user?.expiry_date) })}
          </p>
        </div>
      </div>

      {/* Промокод секция */}
      <div className="promo-section">
        <h3>🎫 {t('dashboard.promo.title')}</h3>
        <div className="promo-form">
          <input
            type="text"
            placeholder={t('dashboard.promo.placeholder')}
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
            className="promo-input"
            disabled={promoLoading}
          />
          <button 
            onClick={applyPromoCode}
            disabled={promoLoading || !promoCode.trim()}
            className="promo-button"
          >
            {promoLoading ? '⏳' : '✨'}
          </button>
        </div>
        {promoMessage && (
          <div className={`promo-message ${promoMessage.includes('успешно') || promoMessage.includes('success') || promoMessage.includes('Получена') ? 'success' : 'error'}`}>
            {promoMessage}
          </div>
        )}
      </div>

      {/* Установка приложения */}
      <div className="app-installation-card">
        <h3>📱 {t('dashboard.vpn.installTitle')}</h3>
        <p className="installation-description">
          {t('dashboard.vpn.installDescription')}
        </p>

        <p className="installation-app-label">sing-box</p>
        <div className="installation-buttons">
          <a 
            href="https://apps.apple.com/us/app/sing-box-vt/id6673731168"
            target="_blank"
            rel="noopener noreferrer"
            className="install-btn ios-btn"
          >
            <span className="btn-icon">📱</span>
            <span className="btn-content">
              <span className="btn-title">{t('dashboard.vpn.iosButton')}</span>
              <span className="btn-subtitle">{t('dashboard.vpn.iosSubtitle')}</span>
            </span>
          </a>
          <a 
            href="https://play.google.com/store/apps/details?id=io.nekohasekai.sfa"
            target="_blank"
            rel="noopener noreferrer"
            className="install-btn android-btn"
          >
            <span className="btn-icon">🤖</span>
            <span className="btn-content">
              <span className="btn-title">{t('dashboard.vpn.androidButton')}</span>
              <span className="btn-subtitle">{t('dashboard.vpn.androidSubtitle')}</span>
            </span>
          </a>
        </div>
        <button 
          onClick={() => setShowSingBoxGuide(true)}
          className="guide-link-btn"
        >
          📖 {t('dashboard.vpn.guideButton')}
        </button>

        <p className="installation-app-label" style={{marginTop: '1rem'}}>Hiddify</p>
        <div className="installation-buttons">
          <a 
            href="https://apps.apple.com/us/app/hiddify-proxy-vpn/id6596777532"
            target="_blank"
            rel="noopener noreferrer"
            className="install-btn ios-btn"
          >
            <span className="btn-icon">📱</span>
            <span className="btn-content">
              <span className="btn-title">{t('dashboard.vpn.iosButton')}</span>
              <span className="btn-subtitle">{t('dashboard.vpn.iosSubtitle')}</span>
            </span>
          </a>
          <a 
            href="https://play.google.com/store/apps/details?id=app.hiddify.com"
            target="_blank"
            rel="noopener noreferrer"
            className="install-btn android-btn"
          >
            <span className="btn-icon">🤖</span>
            <span className="btn-content">
              <span className="btn-title">{t('dashboard.vpn.androidButton')}</span>
              <span className="btn-subtitle">{t('dashboard.vpn.androidSubtitle')}</span>
            </span>
          </a>
        </div>
        <a
          href="https://github.com/hiddify/hiddify-app/releases/latest"
          target="_blank"
          rel="noopener noreferrer"
          className="guide-link-btn"
          style={{textDecoration: 'none', display: 'block', textAlign: 'center'}}
        >
          🖥️ {t('dashboard.vpn.hiddifyDesktop')}
        </a>
      </div>

      {/* VPN конфигурации */}
      {new Date(user?.expiry_date) > new Date() && (
        <div className="configs-section">
          <h3>🌍 {t('dashboard.vpn.configTitle')}</h3>
          <p className="subscription-description">
            📋 {t('dashboard.vpn.configDescription')}
            <br/>{t('dashboard.vpn.servers')}
          </p>
          <div className="download-buttons">
            <button 
              onClick={() => addToSingBox('subscription')}
              className="singbox-button"
            >
              🌍 {t('dashboard.vpn.addSubscription')}
            </button>
            <button 
              onClick={() => addToHiddify()}
              className="hiddify-button"
            >
              🟡 {t('dashboard.vpn.addHiddify')}
            </button>
          </div>
          <div className="subscription-features">
            <div className="feature">✅ {t('dashboard.vpn.features.allProtocols')}</div>
            <div className="feature">✅ {t('dashboard.vpn.features.autoSwitch')}</div>
            <div className="feature">✅ {t('dashboard.vpn.features.dpiBypass')}</div>
            <div className="feature">✅ {t('dashboard.vpn.features.russiaOptimized')}</div>
          </div>
        </div>
      )}

      {/* OpenConnect VPN - отдельная секция */}
      {new Date(user?.expiry_date) > new Date() && (
        <div className="openconnect-section">
          <h3>🔧 {t('dashboard.openconnect.title')}</h3>
          <p className="section-description">
            📡 {t('dashboard.openconnect.description')}
          </p>
          <button 
            onClick={showOpenConnectConfig}
            className="openconnect-button"
            disabled={openconnectLoading}
          >
            {openconnectLoading ? `⏳ ${t('dashboard.openconnect.loading')}` : `🔧 ${t('dashboard.openconnect.getConfig')}`}
          </button>
        </div>
      )}
        </div>
      );

  // Рендер Payment
  const renderPayment = () => (
    <div className="tab-content">
      <div className="payment-header">
        <h2>💳 {t('payment.title')}</h2>
        <p>{t('payment.subtitle')}</p>
      </div>

      <div className="payment-methods">
        <h3>{t('payment.methods.title')}</h3>
        <div className="payment-options">
          <div
            className={`payment-option ${selectedPaymentMethod === 'rollypay' ? 'selected' : ''}`}
            onClick={() => setSelectedPaymentMethod('rollypay')}
          >
            <div className="payment-icon">💳</div>
            <div className="payment-details">
              <div className="payment-name">{t('payment.methods.rollypay.name')}</div>
              <div className="payment-desc">{t('payment.methods.rollypay.desc')}</div>
            </div>
          </div>

          <div
            className={`payment-option ${selectedPaymentMethod === 'telegram_stars' ? 'selected' : ''}`}
            onClick={() => setSelectedPaymentMethod('telegram_stars')}
          >
            <div className="payment-icon">⭐</div>
            <div className="payment-details">
              <div className="payment-name">{t('payment.methods.stars.name')}</div>
              <div className="payment-desc">{t('payment.methods.stars.desc')}</div>
            </div>
          </div>

          <div
            className={`payment-option ${selectedPaymentMethod === 'yoomoney' ? 'selected' : ''}`}
            onClick={() => setSelectedPaymentMethod('yoomoney')}
          >
            <div className="payment-icon">💳</div>
            <div className="payment-details">
              <div className="payment-name">{t('payment.methods.yoomoney.name')}</div>
              <div className="payment-desc">{t('payment.methods.yoomoney.desc')}</div>
            </div>
          </div>
          
          <div 
            className={`payment-option ${selectedPaymentMethod === 'ton' ? 'selected' : ''}`}
            onClick={() => setSelectedPaymentMethod('ton')}
          >
            <div className="payment-icon">💎</div>
            <div className="payment-details">
              <div className="payment-name">{t('payment.methods.ton.name')}</div>
              <div className="payment-desc">{t('payment.methods.ton.desc')}</div>
            </div>
          </div>

        </div>
      </div>

      <div className="plans-grid">
        <div className="plan-card">
          <div className="plan-header">
            <h3>{t('payment.plans.month.title')}</h3>
            <div className="plan-price">
              {getPriceDisplay('vpn_month')}
            </div>
            {selectedPaymentMethod !== 'yoomoney' && selectedPaymentMethod !== 'rollypay' && (
              <div className="plan-price-sub">≈ {getRubPrice('vpn_month')}₽</div>
            )}
          </div>
          <div className="plan-features">
            <div className="feature">✅ {t('payment.plans.features.unlimited')}</div>
            <div className="feature">✅ {t('payment.plans.features.allProtocols')}</div>
            <div className="feature">✅ {t('payment.plans.features.support')}</div>
          </div>
          <button 
            onClick={() => createPayment('vpn_month', selectedPaymentMethod === 'telegram_stars' ? getStarsPrice('vpn_month') : getRubPrice('vpn_month'))}
            className="plan-button"
          >
            {t('payment.plans.selectButton')}
          </button>
        </div>

        <div className="plan-card featured">
          <div className="plan-badge">{t('payment.plans.year.badge')}</div>
          <div className="plan-header">
            <h3>{t('payment.plans.year.title')}</h3>
            <div className="plan-price">
              {getPriceDisplay('vpn_year')}
            </div>
            {selectedPaymentMethod !== 'yoomoney' && selectedPaymentMethod !== 'rollypay' && (
              <div className="plan-price-sub">≈ {getRubPrice('vpn_year')}₽</div>
            )}
            <div className="plan-discount">{t('payment.plans.year.discount')}</div>
          </div>
          <div className="plan-features">
            <div className="feature">✅ {t('payment.plans.features.unlimited')}</div>
            <div className="feature">✅ {t('payment.plans.features.allProtocols')}</div>
            <div className="feature">✅ {t('payment.plans.features.prioritySupport')}</div>
            <div className="feature">✅ {t('payment.plans.features.savings')}</div>
          </div>
          <button 
            onClick={() => createPayment('vpn_year', selectedPaymentMethod === 'telegram_stars' ? getStarsPrice('vpn_year') : getRubPrice('vpn_year'))}
            className="plan-button featured"
          >
            {t('payment.plans.selectButton')}
          </button>
          </div>
        </div>

      {/* TON Payment Modal */}
      {showTonPayment && tonPaymentPlanId && (
        <div className="ton-payment-modal">
          <div className="ton-payment-modal-overlay" onClick={handleTonCancel} />
          <div className="ton-payment-modal-content">
            <TonPayment
              planId={tonPaymentPlanId}
              authData={tonAuthData}
              apiBase={API_BASE_URL}
              onSuccess={handleTonSuccess}
              onError={handleTonError}
              onCancel={handleTonCancel}
            />
          </div>
        </div>
      )}
            </div>
  );

  // Рендер Support
  const renderSupport = () => (
    <div className="tab-content">
      <div className="support-header">
        <h2>💬 {t('support.title')}</h2>
        <p>{t('support.subtitle')}</p>
      </div>

      <div className="support-options">
        {/* Главная карточка - Тикеты поддержки */}
        <div className="support-card featured">
          <div className="support-icon">📋</div>
          <h3>{t('support.tickets.title')}</h3>
          <p>{t('support.tickets.description')}</p>
          <button 
            onClick={() => setShowSupportTickets(true)}
            className="support-button primary"
          >
            {t('support.tickets.openButton')}
          </button>
        </div>
        
        <div className="support-card">
          <div className="support-icon">📞</div>
          <h3>{t('support.bot.title')}</h3>
          <p>{t('support.bot.description')}</p>
          <button 
            onClick={() => {
              if (window.Telegram?.WebApp) {
                window.Telegram.WebApp.openTelegramLink('https://t.me/shurik_vpn_support_bot');
              }
            }}
            className="support-button"
          >
            {t('support.bot.button')}
          </button>
        </div>
          
        <div className="support-card">
          <div className="support-icon">📖</div>
          <h3>{t('support.faq.title')}</h3>
          <p>{t('support.faq.description')}</p>
          <button 
            onClick={() => {
              if (window.Telegram?.WebApp) {
                window.Telegram.WebApp.openLink('https://vpn-admin.shurik.ch/support');
              }
            }}
            className="support-button"
          >
            {t('support.faq.button')}
          </button>
        </div>

        <div className="support-card">
          <div className="support-icon">⚙️</div>
          <h3>{t('support.setup.title')}</h3>
          <p>{t('support.setup.description')}</p>
          <button 
            onClick={() => setShowSingBoxGuide(true)}
            className="support-button"
          >
            {t('support.setup.button')}
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <>
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
      {DEBUG_MODE && <DebugLogger />}
    </>
    );
  }

  if (error) {
    return (
      <>
      <div className="error-container">
        <div className="error-icon">❌</div>
        <h2>{t('common.error')}</h2>
        <p>{error}</p>
        <button onClick={() => {
          // Пытаемся принудительную авторизацию, потом перезагружаем
          const telegramData = window.Telegram?.WebApp?.initDataUnsafe?.user;
          if (telegramData) {
            sendLog('info', 'Manual force auth triggered', { userId: telegramData.id });
            forceAuth(telegramData, false, 'main').then(() => {
              setTimeout(() => window.location.reload(), 2000);
            }).catch(() => {
              // Если принудительная авторизация не помогла - просто перезагружаем
              window.location.reload();
            });
          } else {
            window.location.reload();
          }
        }} className="retry-button">
          🔧 {t('errors.forceAuth')}
        </button>
        <button onClick={() => window.location.reload()} className="retry-button" style={{marginTop: '10px', background: '#666'}}>
          🔄 {t('errors.normalReload')}
        </button>
                </div>
      {DEBUG_MODE && <DebugLogger />}
    </>
    );
  }

  return (
    <div className="app">
      <div className="app-content">
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'payment' && renderPayment()}
        {activeTab === 'support' && renderSupport()}
      </div>

      {/* Информация о версии */}
      <div className="version-info">
        <div className="version-badge">
          <span className="version-text">v{FULL_VERSION}</span>
          <LanguageSwitcher />
          <span className="server-source">{SERVER_SOURCE}</span>
          {DEBUG_MODE && ENABLE_SMART_ROUTER && (
            <span className="api-router" title="Smart API Router активен">🔄</span>
          )}
        </div>
      </div>

      {/* Нижняя навигация */}
      <div className="bottom-navigation">
        <button 
          onClick={() => setActiveTab('dashboard')}
          className={`nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
        >
          <div className="nav-icon">📊</div>
          <div className="nav-label">{t('nav.dashboard')}</div>
        </button>
        
        <button 
          onClick={() => setActiveTab('payment')}
          className={`nav-item ${activeTab === 'payment' ? 'active' : ''}`}
        >
          <div className="nav-icon">💳</div>
          <div className="nav-label">{t('nav.payment')}</div>
        </button>
        
        <button 
          onClick={() => setActiveTab('support')}
          className={`nav-item ${activeTab === 'support' ? 'active' : ''}`}
        >
          <div className="nav-icon">💬</div>
          <div className="nav-label">{t('nav.support')}</div>
        </button>
      </div>
      
      {/* OpenConnect модальное окно */}
      {openconnectModal && openconnectConfig && (
        <div className="modal-overlay" onClick={() => setOpenconnectModal(false)}>
          <div className="modal-content openconnect-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🔧 OpenConnect VPN</h3>
              <button 
                className="modal-close"
                onClick={() => setOpenconnectModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="openconnect-info">
                <p>📡 <strong>Классический VPN протокол</strong> - работает везде, стабильный и надежный</p>
              </div>
              
              <div className="connection-data">
                <h4>📋 {t('openconnectModal.connectionData')}</h4>
                
                <div className="data-row">
                  <span className="data-label">{t('openconnectModal.server')}:</span>
                  <span className="data-value">{openconnectConfig.server}</span>
                  <button 
                    className="copy-btn"
                    onClick={() => copyToClipboard(openconnectConfig.server, t('openconnectModal.server'))}
                  >
                    📋
                  </button>
                </div>
                
                <div className="data-row">
                  <span className="data-label">{t('openconnectModal.login')}:</span>
                  <span className="data-value">{openconnectConfig.username}</span>
                  <button 
                    className="copy-btn"
                    onClick={() => copyToClipboard(openconnectConfig.username, t('openconnectModal.login'))}
                  >
                    📋
                  </button>
                </div>
                
                <div className="data-row">
                  <span className="data-label">{t('openconnectModal.password')}:</span>
                  <span className="data-value">{openconnectConfig.password}</span>
                  <button 
                    className="copy-btn"
                    onClick={() => copyToClipboard(openconnectConfig.password, t('openconnectModal.password'))}
                  >
                    📋
                  </button>
                </div>
                
                <div className="copy-all-section">
                  <button 
                    className="copy-all-btn"
                    onClick={() => {
                      const allData = `${t('openconnectModal.server')}: ${openconnectConfig.server}\n${t('openconnectModal.login')}: ${openconnectConfig.username}\n${t('openconnectModal.password')}: ${openconnectConfig.password}`;
                      copyToClipboard(allData, t('openconnectModal.copyAll'));
                    }}
                  >
                    📋 {t('openconnectModal.copyAll')}
                  </button>
                </div>
              </div>
              
              <div className="apps-section">
                <h4>📱 Рекомендуемые приложения:</h4>
                
                <div className="app-card">
                  <div className="app-header">
                    <span className="app-platform">📱 iOS</span>
                    <a 
                      href={openconnectConfig.client_apps.ios.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="download-app-btn"
                    >
                      Скачать {openconnectConfig.client_apps.ios.name}
                    </a>
                  </div>
                </div>
                
                <div className="app-card">
                  <div className="app-header">
                    <span className="app-platform">🤖 Android</span>
                    <a 
                      href={openconnectConfig.client_apps.android.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="download-app-btn"
                    >
                      Скачать {openconnectConfig.client_apps.android.name}
                    </a>
                  </div>
                </div>
                
                <div className="app-card">
                  <div className="app-header">
                    <span className="app-platform">💻 Windows/macOS</span>
                    <a 
                      href={openconnectConfig.client_apps.windows.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="download-app-btn"
                    >
                      Скачать {openconnectConfig.client_apps.windows.name}
                    </a>
                  </div>
                </div>
              </div>
              
              <div className="instructions-section">
                <h4>📖 Инструкция по подключению:</h4>
                <ol className="instructions-list">
                  <li>Установите приложение для вашей платформы</li>
                  <li>Введите сервер: <code>{openconnectConfig.server}</code></li>
                  <li>Введите логин и пароль (используйте кнопки копирования выше)</li>
                  <li>Подключитесь к VPN</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {showSingBoxGuide && (
        <SingBoxGuide 
          onClose={() => setShowSingBoxGuide(false)}
          onAddSubscription={(protocol) => addToSingBox(protocol)}
        />
      )}
      
      {/* Support Tickets Modal */}
      {showSupportTickets && (
        <SupportTickets 
          authData={authData?.user}
          initData={window.Telegram?.WebApp?.initData}
          onClose={() => setShowSupportTickets(false)}
        />
      )}
      
      {/* Debug Logger для отладки в Telegram (только в DEBUG режиме) */}
      {DEBUG_MODE && <DebugLogger />}
    </div>
  );
}

// 🔧 ПРИНУДИТЕЛЬНАЯ АВТОРИЗАЦИЯ для проблемных сетей (Beeline)
const forceAuth = async (userTelegram, isBeeline, preferredServer) => {
  try {
    debugLog('🔥 Принудительная авторизация для проблемных сетей...', {
      isBeeline,
      preferredServer,
      userId: userTelegram.id
    });
    
    // Используем реальные данные Telegram если доступны
    const authData = {
      initData: window.Telegram?.WebApp?.initData || `user=${JSON.stringify(userTelegram)}`,
      id: userTelegram.id,
      first_name: userTelegram.first_name || 'MiniApp',
      last_name: userTelegram.last_name || 'User',
      username: userTelegram.username || `user_${userTelegram.id}`
    };
    
    debugLog('🧪 Отправка принудительного auth запроса...');
    
    const response = await apiFetch('/api/telegram/auth', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'X-Force-Auth': 'true',
        'X-Is-Beeline': isBeeline ? 'true' : 'false',
        'X-Preferred-Server': preferredServer,
        'X-User-Agent': navigator.userAgent.substring(0, 100)
      },
      body: JSON.stringify(authData)
    });
    
    debugLog(`📡 Force auth response: ${response.status}`);
    
    if (response.ok) {
      const data = await response.json();
      debugLog(`✅ Force auth успешен:`, data);
      
      if (data.success) {
        debugLog('🎉 Принудительная авторизация сработала! Перезагружаем...');
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } else {
      throw new Error(`HTTP ${response.status}`);
    }
    
  } catch (error) {
    console.error(`❌ Force auth failed:`, error);
  }
};

export default App;